import React, { useEffect, useState } from "react";
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from "chart.js";
import axiosInstance from "utils/axiosInstance";
import apiConfig from "../../config/appConfig";

// Register Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
);

const RequestStatusPieChart = () => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get(apiConfig.endpoints.dashboard.statusDistribution);
        const distribution = response.data;

        setChartData({
          labels: ["Success", "Failure"],
          datasets: [
            {
              data: [distribution.success, distribution.failure],
              backgroundColor: [theme.palette.success.main, theme.palette.error.main],
              hoverBackgroundColor: [theme.palette.success.dark, theme.palette.error.dark],
              borderWidth: isMobile ? 1 : 2,
              borderColor: theme.palette.background.paper,
            },
          ],
        });
      } catch (err) {
        console.error("Error fetching request status distribution:", err);
        setError(err.message || "Failed to fetch request status distribution.");
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [isMobile, theme.palette.success.main, theme.palette.error.main, theme.palette.background.paper]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: isMobile ? 10 : 12,
          },
          padding: isMobile ? 10 : 20,
          usePointStyle: true,
        },
      },
      tooltip: {
        titleFont: {
          size: isMobile ? 10 : 12,
        },
        bodyFont: {
          size: isMobile ? 10 : 12,
        },
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          }
        }
      },
    },
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: 150,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: { xs: 1, sm: 2 } }}>
        <Alert severity="error" sx={{ fontSize: '0.75rem' }}>{error}</Alert>
      </Box>
    );
  }

  if (!chartData) {
    return (
      <Box sx={{ 
        padding: { xs: 1, sm: 2 },
        textAlign: "center",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <Typography variant="body2" color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: "100%", 
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}>
      <Typography 
        variant={isMobile ? "h6" : "h5"} 
        align="center" 
        gutterBottom
        sx={{ 
          fontSize: { xs: '1rem', sm: '1.25rem' },
          fontWeight: 600,
          mb: 1
        }}
      >
        Request Status
      </Typography>
      
      <Box sx={{ 
        flexGrow: 1,
        position: "relative",
        minHeight: 120,
      }}>
        <Pie data={chartData} options={chartOptions} />
      </Box>
    </Box>
  );
};

export default RequestStatusPieChart;